import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery, gql } from "@apollo/client";
import {
  Box,
  Container,
  Fade,
  Skeleton,
  Alert,
  AlertTitle,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { debounce } from "lodash";
import SearchRecipe from "../Components/SearchRecipe";
import ListOfRecipes from "../Components/ListOfRecipes";
import WhatWeCookToday from "../Components/DashboardComponent/WhatWeCookToday";
import {
  AnimatedContainer,
  AnimatedBackground,
} from "../Components/AnimatedContainer";

const GET_RECIPES = gql`
  query GetRecipes($searchTerm: String) {
    allRecipes(searchTerm: $searchTerm) {
      id
      recipe_name
      viewsCount
      sharedCount
      likedCount
      cookedCount
      downloadedCount
      created_by
      duration
      recipe_category
      recipe_type
      recipe_status
      recipe_visiblity
      serving
      prep_time
      cook_time
      isLiked
      averageRating
      ratingCount
      recipe_image
      profile_pic
    }
  }
`;

// Enhanced loading skeleton for better UX
const DashboardSkeleton = () => {
  const theme = useTheme();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header skeleton */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Skeleton
            variant="text"
            width="60%"
            height={80}
            sx={{
              mx: "auto",
              mb: 2,
              borderRadius: 2,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(90deg, ${theme.palette.grey[800]} 25%, ${theme.palette.grey[700]} 50%, ${theme.palette.grey[800]} 75%)`
                  : `linear-gradient(90deg, ${theme.palette.grey[300]} 25%, ${theme.palette.grey[200]} 50%, ${theme.palette.grey[300]} 75%)`,
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
              "@keyframes shimmer": {
                "0%": { backgroundPosition: "-200% 0" },
                "100%": { backgroundPosition: "200% 0" },
              },
            }}
          />
          <Skeleton
            variant="text"
            width="40%"
            height={40}
            sx={{ mx: "auto", borderRadius: 2 }}
          />
        </Box>

        {/* Search skeleton */}
        <Box sx={{ maxWidth: 600, mx: "auto", mb: 4 }}>
          <Skeleton variant="rounded" height={64} sx={{ borderRadius: 4 }} />
        </Box>

        {/* Content skeleton */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 3,
          }}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Skeleton
                variant="rounded"
                height={400}
                sx={{ borderRadius: 3 }}
              />
            </motion.div>
          ))}
        </Box>
      </motion.div>
    </Container>
  );
};

// Enhanced error component
const ErrorDisplay = ({ error, onRetry }) => {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert
          severity="error"
          sx={{
            borderRadius: 4,
            p: 4,
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.error.dark}15 0%, ${theme.palette.background.paper} 100%)`
                : `linear-gradient(135deg, ${theme.palette.error.light}15 0%, ${theme.palette.background.paper} 100%)`,
            border: `1px solid ${theme.palette.error.main}30`,
            "& .MuiAlert-icon": {
              fontSize: "2rem",
            },
          }}
        >
          <AlertTitle sx={{ fontSize: "1.5rem", fontWeight: 700, mb: 2 }}>
            Oops! Something went wrong
          </AlertTitle>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            {error?.message ||
              "We couldn't load the recipes. Please try again."}
          </Typography>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onRetry}
            style={{
              background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
              color: "white",
              border: "none",
              borderRadius: "12px",
              padding: "12px 24px",
              fontSize: "16px",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: `0 4px 16px ${theme.palette.error.main}40`,
            }}
          >
            Try Again
          </motion.button>
        </Alert>
      </Container>
    </motion.div>
  );
};

const DashboardNewPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const recipesPerPage = isMobile ? 6 : isTablet ? 9 : 12;

  // Enhanced debounced search with loading state
  const debouncedSearch = useCallback(
    debounce((term) => {
      setDebouncedSearchTerm(term);
      setIsSearching(false);
      setCurrentPage(1);
    }, 800),
    []
  );

  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
    debouncedSearch(searchTerm);

    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch, debouncedSearchTerm]);

  // Memoize query options for better performance
  const queryOptions = useMemo(
    () => ({
      variables: { searchTerm: debouncedSearchTerm },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      errorPolicy: "all",
    }),
    [debouncedSearchTerm]
  );

  const { loading, error, data, refetch, networkStatus } = useQuery(
    GET_RECIPES,
    queryOptions
  );

  // Simple filtering without complex state management
  const allRecipes = data?.allRecipes || [];

  // Enhanced pagination with smooth transitions
  const paginationData = useMemo(() => {
    const indexOfLastRecipe = currentPage * recipesPerPage;
    const indexOfFirstRecipe = indexOfLastRecipe - recipesPerPage;
    return {
      currentRecipes: allRecipes.slice(indexOfFirstRecipe, indexOfLastRecipe),
      totalPages: Math.ceil(allRecipes.length / recipesPerPage),
    };
  }, [currentPage, allRecipes, recipesPerPage]);

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
    // Smooth scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  }, []);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Show skeleton on initial load
  if (loading && !data) {
    return <DashboardSkeleton />;
  }

  // Show error state
  if (error && !data) {
    return <ErrorDisplay error={error} onRetry={handleRetry} />;
  }

  return (
    <AnimatedContainer theme={theme}>
      <AnimatedBackground theme={theme} />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <Box sx={{ position: "relative", width: "100%", zIndex: 1 }}>
          {/* Enhanced Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <WhatWeCookToday />
          </motion.div>

          {/* Enhanced Search Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <SearchRecipe
              setSearchTerm={handleSearchChange}
              isSearching={isSearching}
              searchTerm={searchTerm}
            />
          </motion.div>

          {/* Enhanced Recipe List Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <Fade in={!isSearching} timeout={600}>
              <div>
                <ListOfRecipes
                  loading={loading || isSearching}
                  error={error}
                  currentRecipes={paginationData.currentRecipes}
                  handlePageChange={handlePageChange}
                  currentPage={currentPage}
                  totalPages={paginationData.totalPages}
                  refetch={refetch}
                  searchTerm={searchTerm}
                  totalRecipes={allRecipes.length}
                  isSearchActive={!!searchTerm}
                />
              </div>
            </Fade>
          </motion.div>

          {/* Loading overlay for search */}
          <AnimatePresence>
            {isSearching && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.3)",
                  backdropFilter: "blur(4px)",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  style={{
                    background: theme.palette.background.paper,
                    borderRadius: "16px",
                    padding: "24px 32px",
                    boxShadow: `0 20px 60px rgba(0,0,0,0.3)`,
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    style={{
                      width: "24px",
                      height: "24px",
                      border: `3px solid ${theme.palette.primary.main}30`,
                      borderTop: `3px solid ${theme.palette.primary.main}`,
                      borderRadius: "50%",
                    }}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    Searching recipes...
                  </Typography>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </motion.div>
    </AnimatedContainer>
  );
};

export default React.memo(DashboardNewPage);
